import { createTag, fetchTags } from '@/api/tags'
import type { OrgTag } from '@/api/tags'
import { RagFileUploadState, addRagFileWithResult, fetchRagFiles } from '@/api/content/file'
import type { AddRagFileResult, FetchRagFiles, GetRagFileResponse } from '@/api/content/file'
import { fetchRagTexts } from '@/api/content/text'
import { textPreview } from '@/components/proposal/proposalText'
import type {
  ConversationComposerLoadRequest,
  ConversationComposerOption,
  ConversationComposerPage,
  ConversationComposerUploadResult
} from './Composer'

function tagOption(tag: OrgTag): ConversationComposerOption {
  return {
    id: tag.tagId,
    label: tag.name,
    ...(tag.description ? { secondary: tag.description } : {})
  }
}

export async function loadConversationTags(
  request: ConversationComposerLoadRequest = { page: 1, search: '' }
): Promise<Array<ConversationComposerOption>> {
  const tags = await fetchTags()
  const search = request.search.trim().toLowerCase()
  return tags
    .filter((tag) => tag.name.toLowerCase().includes(search) || tag.description?.toLowerCase().includes(search))
    .map(tagOption)
}

export async function createConversationTag(name: string): Promise<ConversationComposerOption> {
  return tagOption(await createTag(name))
}

// The picker lists every workspace document a brief may draw on, files and
// pasted text alike. Both listings share the doc id namespace and are
// recency-sorted server-side; merging them page by page keeps that order
// roughly intact while either listing still has pages.
export async function loadConversationDocuments(
  projectId: number,
  request: ConversationComposerLoadRequest = { page: 1, search: '' }
): Promise<ConversationComposerPage> {
  const search = request.search.trim()
  const searchParams = search === '' ? {} : { searchText: search }
  const [files, texts] = await Promise.all([
    fetchRagFiles({ projectId, currentPage: request.page, ...searchParams }),
    fetchRagTexts({ projectId, currentPage: request.page, ...searchParams })
  ])
  const items: Array<ConversationComposerOption> = [
    ...files.items
      .filter((file) => file.fileUploadState === RagFileUploadState.Complete)
      .map((file) => ({
        id: file.fileId,
        label: file.fileName,
        updatedAtTsMs: file.updatedAtTsMs,
        ...(file.description ? { secondary: file.description } : {})
      })),
    ...texts.items.map((text) => ({
      id: text.docId,
      label: textPreview(text.text),
      secondary: 'Pasted text',
      updatedAtTsMs: text.updatedAtTsMs
    }))
  ].sort((a, b) => b.updatedAtTsMs - a.updatedAtTsMs)
  return {
    items,
    hasMore: request.page < Math.max(files.pageCount, texts.pageCount)
  }
}

export type ConversationDocumentUploadServices = {
  addFile: (file: File, description: string, projectId?: number) => Promise<AddRagFileResult>
  loadFiles: (params?: Partial<FetchRagFiles>) => Promise<GetRagFileResponse>
  wait: (delayMs: number) => Promise<void>
  maxPolls: number
}

const conversationDocumentUploadServices: ConversationDocumentUploadServices = {
  addFile: addRagFileWithResult,
  loadFiles: fetchRagFiles,
  wait: (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
  maxPolls: 60
}

function completedUpload(result: AddRagFileResult): ConversationComposerUploadResult {
  return { document: { id: result.fileId, label: result.fileName }, reused: result.reused }
}

function processingFailure(result: AddRagFileResult): Error & { retryable: false } {
  return Object.assign(
    new Error(
      result.reused
        ? `${result.fileName} is already in Content but could not be processed. Retry it from Content.`
        : `${result.fileName} was uploaded to Content but could not be processed. Retry it from Content.`
    ),
    { retryable: false as const }
  )
}

export async function uploadConversationDocument(
  projectId: number,
  file: File,
  services: ConversationDocumentUploadServices = conversationDocumentUploadServices
): Promise<ConversationComposerUploadResult> {
  const result = await services.addFile(file, '', projectId)
  if (result.fileUploadState === RagFileUploadState.Complete) return completedUpload(result)
  if (result.fileUploadState === RagFileUploadState.Error) throw processingFailure(result)

  for (let poll = 0; poll < services.maxPolls; poll += 1) {
    await services.wait(1500)
    const response = await services.loadFiles({ currentPage: 1, docId: result.fileId, projectId })
    const current = response.items.find((item) => item.fileId === result.fileId)
    if (current === undefined) {
      throw new Error(`${result.fileName} could not be found in this workspace.`)
    }
    if (current.fileUploadState === RagFileUploadState.Complete) {
      return {
        document: { id: current.fileId, label: current.fileName },
        reused: result.reused
      }
    }
    if (current.fileUploadState === RagFileUploadState.Error) throw processingFailure(result)
  }

  throw new Error(`${result.fileName} is still processing. Attach it from Content when it is ready.`)
}
